import React, { useState, useEffect } from 'react';
import { Box, Container, BottomNavigation, BottomNavigationAction, useTheme, Badge } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import StoreIcon from '@mui/icons-material/Store';
import KitchenIcon from '@mui/icons-material/Kitchen';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InsightsIcon from '@mui/icons-material/Insights';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useShoppingMode } from './ShoppingModeContext';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

const navItems: NavItem[] = [
  { label: 'Stores', icon: <StoreIcon />, path: '/stores' },
  { label: 'Groceries', icon: <KitchenIcon />, path: '/groceries' },
  { label: 'Prices', icon: <AttachMoneyIcon />, path: '/prices' },
  { label: 'Lists', icon: <ListAltIcon />, path: '/shopping-lists' },
  { label: 'Activity', icon: <InsightsIcon />, path: '/shopping-activity' },
  { label: 'Shop', icon: <ShoppingCartIcon />, path: '/going-shopping' },
];

const BottomNav: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { shoppingMode } = useShoppingMode();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    Boolean(localStorage.getItem('access_token'))
  );

  // Listen for auth changes
  useEffect(() => {
    const handleAuthChange = () => {
      setIsAuthenticated(Boolean(localStorage.getItem('access_token')));
    };
    window.addEventListener('authChange', handleAuthChange);
    return () => {
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, []);

  // Don't show nav if not authenticated
  if (!isAuthenticated) return null;

  // Determine current nav value based on path
  const getCurrentValue = () => {
    const currentPath = location.pathname;
    const index = navItems.findIndex(item => currentPath.startsWith(item.path));
    return index >= 0 ? index : -1;
  };

  const handleNavChange = (_: React.SyntheticEvent, newValue: number) => {
    // For Shop nav (index 5), pass state to go to active list if one exists
    if (newValue === 5 && shoppingMode.isActive) {
      navigate(navItems[newValue].path, { state: { goToActiveList: true } });
    } else {
      navigate(navItems[newValue].path);
    }
  };

  // Get icon with shopping mode indicator for Shop nav item
  const getNavIcon = (item: NavItem, index: number) => {
    // Shop is the last item (index 5)
    if (index === 5 && shoppingMode.isActive) {
      return (
        <Badge
          variant="dot"
          color="info"
          sx={{
            '& .MuiBadge-badge': {
              right: -2,
              top: 2,
            },
          }}
        >
          {item.icon}
        </Badge>
      );
    }
    return item.icon;
  };

  return (
    <>
      {/* Spacer to prevent content from being hidden behind fixed nav */}
      <Box sx={{ height: 56 }} />
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="md" disableGutters>
          <BottomNavigation
            value={getCurrentValue()}
            onChange={handleNavChange}
            showLabels
            sx={{
              bgcolor: 'transparent',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                padding: '6px 0',
                color: theme.palette.primary.main,
                '&.Mui-selected': {
                  color: theme.palette.primary.dark,
                },
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                '&.Mui-selected': {
                  fontSize: '0.75rem',
                },
              },
            }}
          >
            {navItems.map((item, index) => (
              <BottomNavigationAction
                key={item.path}
                label={item.label}
                icon={getNavIcon(item, index)}
                sx={index === 5 && shoppingMode.isActive ? {
                  color: `${theme.palette.info.main} !important`,
                  '&.Mui-selected': {
                    color: `${theme.palette.info.dark} !important`,
                  },
                } : {}}
              />
            ))}
          </BottomNavigation>
        </Container>
      </Box>
    </>
  );
};

export default BottomNav;